import type { Asset } from './types';
import { assetPreviewId } from './utils.ts';

export type PreviewIndexChange =
    | { kind: 'unchanged' }
    | { kind: 'follow'; index: number }
    | { kind: 'open'; index: number }
    | { kind: 'close' };

const UNCHANGED: PreviewIndexChange = { kind: 'unchanged' };

// 'follow' keeps the shown file at its new index; 'open' loads the file now at the
// shown file's slot because the shown file left the list.
export const nextPreviewIndex = (
    previewableAssets: readonly Asset[],
    shownId: string | null,
    previewIndex: number | null
): PreviewIndexChange => {
    if (previewIndex === null) return UNCHANGED;
    if (previewableAssets.length === 0) return { kind: 'close' };
    const shownIndex = previewableAssets.findIndex((a) => assetPreviewId(a) === shownId);
    if (shownIndex === previewIndex) return UNCHANGED;
    if (shownIndex !== -1) return { kind: 'follow', index: shownIndex };
    return { kind: 'open', index: Math.min(previewIndex, previewableAssets.length - 1) };
};

// Applies a pending 'follow' at render time so a list refresh never renders a neighbour
// (and unmounts the preview) for the commit before the follow effect runs.
export const displayedPreviewIndex = (
    previewableAssets: readonly Asset[],
    shownId: string | null,
    previewIndex: number | null
): number | null => {
    const change = nextPreviewIndex(previewableAssets, shownId, previewIndex);
    return change.kind === 'follow' ? change.index : previewIndex;
};
